import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Gift, Star, Award, Ticket, ShoppingBag, ExternalLink, Check, Loader2, Package, CreditCard, Heart } from 'lucide-react';

const PointsStore = () => {
  const { user, updateUser } = useAuth();
  const [items, setItems] = useState([]);
  const [userPoints, setUserPoints] = useState({ total_points: 0, available_points: 0, points_spent: 0 });
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [activeTab, setActiveTab] = useState('store');
  
  // Image lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Shipping modal state for physical items
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const [shippingInfo, setShippingInfo] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    province: '',
    postal_code: '',
    phone: '',
    notes: '',
    selected_size: '',
    selected_color: ''
  });
  const [saveToProfile, setSaveToProfile] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, pointsRes] = await Promise.all([
        api.get('/store/items'),
        user ? api.get('/users/me/points') : Promise.resolve({ data: { available_points: 0 } })
      ]);
      
      setItems(itemsRes.data.items || []);
      setUserPoints(pointsRes.data);
      
      if (user) {
        const redemptionsRes = await api.get('/store/my-redemptions');
        setRedemptions(redemptionsRes.data.redemptions || []);
      }
    } catch (error) {
      console.error('Error fetching store data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (item, shippingData = null) => {
    if (!user) {
      toast.error('Please log in to redeem items');
      return;
    }

    if (userPoints.available_points < item.points_cost) {
      toast.error(`Not enough points! You need ${item.points_cost - userPoints.available_points} more points.`);
      return;
    }

    // For physical items, show shipping modal first
    if (item.item_type === 'physical' && !shippingData) {
      setPendingItem(item);
      setShowShippingModal(true);
      return;
    }

    setRedeeming(item.id);
    try {
      const payload = { item_id: item.id };
      if (shippingData) {
        payload.shipping_info = shippingData;
        payload.save_to_profile = saveToProfile;
      }
      
      const response = await api.post('/store/redeem', payload);
      toast.success(response.data.message);
      
      // Update local state
      setUserPoints(prev => ({
        ...prev,
        available_points: response.data.points_remaining,
        points_spent: prev.points_spent + item.points_cost
      }));
      
      // Refresh redemptions
      const redemptionsRes = await api.get('/store/my-redemptions');
      setRedemptions(redemptionsRes.data.redemptions || []);

      // Refresh auth context so cosmetics show immediately on profile
      try {
        const meRes = await api.get('/auth/me');
        updateUser(meRes.data);
      } catch {}

      // If affiliate link, open it
      if (response.data.fulfillment?.affiliate_url) {
        window.open(response.data.fulfillment.affiliate_url, '_blank');
      }
      
      // Close shipping modal if open
      setShowShippingModal(false);
      setPendingItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem item');
    } finally {
      setRedeeming(null);
    }
  };

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    if (pendingItem) {
      handleRedeem(pendingItem, shippingInfo);
    }
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'badge':            return <Award className="w-6 h-6" />;
      case 'ad_credit':        return <Star className="w-6 h-6" />;
      case 'affiliate':        return <Gift className="w-6 h-6" />;
      case 'prize_entry':      return <Ticket className="w-6 h-6" />;
      case 'physical':         return <Package className="w-6 h-6" />;
      case 'gift_card':        return <CreditCard className="w-6 h-6" />;
      case 'charity':          return <Heart className="w-6 h-6" />;
      case 'cosmetic_accent':  return <span className="text-2xl">🎨</span>;
      case 'cosmetic_name_style': return <span className="text-2xl font-bold">B</span>;
      case 'cosmetic_frame':   return <span className="text-2xl">✨</span>;
      case 'cosmetic_section': return <span className="text-2xl">📝</span>;
      default:                 return <ShoppingBag className="w-6 h-6" />;
    }
  };

  const getItemTypeLabel = (type) => {
    switch (type) {
      case 'badge':            return 'Exclusive Badge';
      case 'ad_credit':        return 'Ad Credit';
      case 'affiliate':        return 'Partner Reward';
      case 'prize_entry':      return 'Prize Draw Entry';
      case 'physical':         return 'Physical Item';
      case 'gift_card':        return 'Gift Card';
      case 'charity':          return 'Charity Donation';
      case 'cosmetic_accent':  return 'Profile Accent';
      case 'cosmetic_name_style': return 'Name Style';
      case 'cosmetic_frame':   return 'Avatar Frame';
      case 'cosmetic_section': return 'Profile Unlock';
      default:                 return 'Reward';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A52A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <div className="border-b border-gray-800 bg-gradient-to-r from-gray-900 via-[#1a1a0f] to-gray-900">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-black tracking-tight">
                  POINTS <span className="text-[#C5A52A]">STORE</span>
                </h1>
                <p className="text-gray-400 mt-1">
                  Redeem your civic engagement points for exclusive rewards
                </p>
              </div>
              
              {/* Points Balance */}
              <div className="bg-gray-900 border border-[#C5A52A]/30 rounded-lg p-4 min-w-[200px]">
                <div className="text-sm text-gray-400 font-mono uppercase">Your Balance</div>
                <div className="text-3xl font-black text-[#C5A52A]">
                  {userPoints.available_points?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {userPoints.total_points?.toLocaleString() || 0} total earned
                </div>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setActiveTab('store')}
                className={`px-4 py-2 font-mono text-sm uppercase transition-colors ${
                  activeTab === 'store'
                    ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                Store
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 font-mono text-sm uppercase transition-colors ${
                  activeTab === 'history'
                    ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                My Redemptions
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          {activeTab === 'store' && (
            <>
              {/* How to Earn Points */}
              <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 mb-8">
                <h3 className="font-bold text-lg mb-3">How to Earn Points</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-gray-800/50 rounded">
                    <div className="text-[#C5A52A] font-bold">+10</div>
                    <div className="text-gray-400">Post an Issue</div>
                  </div>
                  <div className="text-center p-3 bg-gray-800/50 rounded">
                    <div className="text-[#C5A52A] font-bold">+20</div>
                    <div className="text-gray-400">Verify a Fix</div>
                  </div>
                  <div className="text-center p-3 bg-gray-800/50 rounded">
                    <div className="text-[#C5A52A] font-bold">Varies</div>
                    <div className="text-gray-400">Earn a Badge</div>
                  </div>
                  <div className="text-center p-3 bg-gray-800/50 rounded">
                    <div className="text-[#C5A52A] font-bold">+5</div>
                    <div className="text-gray-400">Daily Login</div>
                  </div>
                </div>
              </div>

              {/* Store Items */}
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingBag className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-400">Store Coming Soon</h3>
                  <p className="text-gray-500 mt-2">
                    Keep earning points - exciting rewards are on the way!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((item) => {
                    const canAfford = userPoints.available_points >= item.points_cost;
                    const isOutOfStock = item.quantity_available !== null && item.quantity_available <= 0;
                    
                    return (
                      <div 
                        key={item.id}
                        className={`bg-gray-900 border rounded-lg overflow-hidden transition-all ${
                          canAfford && !isOutOfStock
                            ? 'border-gray-700 hover:border-[#C5A52A]/50'
                            : 'border-gray-800 opacity-70'
                        }`}
                      >
                        {/* Item Header */}
                        <div className={`p-4 ${
                          item.item_type === 'badge' ? 'bg-purple-900/20' :
                          item.item_type === 'ad_credit' ? 'bg-blue-900/20' :
                          item.item_type === 'affiliate' ? 'bg-green-900/20' :
                          item.item_type === 'physical' ? 'bg-orange-900/20' :
                          item.item_type === 'gift_card' ? 'bg-pink-900/20' :
                          item.item_type === 'charity' ? 'bg-red-900/20' :
                          'bg-yellow-900/20'
                        }`}>
                          {item.image_url ? (
                            <img 
                              src={item.image_url} 
                              alt={item.name}
                              onClick={() => setLightboxImage(item.image_url)}
                              className="w-full h-32 object-cover rounded mb-3 cursor-zoom-in hover:opacity-90 transition-opacity"
                            />
                          ) : null}
                          <div className="flex items-center justify-between">
                            <div className={`p-2 rounded ${
                              item.item_type === 'badge' ? 'bg-purple-500/20 text-purple-400' :
                              item.item_type === 'ad_credit' ? 'bg-blue-500/20 text-blue-400' :
                              item.item_type === 'affiliate' ? 'bg-green-500/20 text-green-400' :
                              item.item_type === 'physical' ? 'bg-orange-500/20 text-orange-400' :
                              item.item_type === 'gift_card' ? 'bg-pink-500/20 text-pink-400' :
                              item.item_type === 'charity' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {getItemIcon(item.item_type)}
                            </div>
                            <span className="text-xs font-mono text-gray-500 uppercase">
                              {getItemTypeLabel(item.item_type)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Item Body */}
                        <div className="p-4">
                          <h3 className="font-bold text-lg mb-2">{item.name}</h3>
                          <p className="text-gray-400 text-sm mb-4">{item.description}</p>
                          
                          {/* Stock indicator */}
                          {item.quantity_available !== null && (
                            <div className="text-xs font-mono text-gray-500 mb-3">
                              {isOutOfStock ? (
                                <span className="text-red-500">OUT OF STOCK</span>
                              ) : (
                                <span>Only {item.quantity_available} left!</span>
                              )}
                            </div>
                          )}
                          
                          {/* Price and Action */}
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-2xl font-black text-[#C5A52A]">
                                {item.points_cost.toLocaleString()}
                              </span>
                              <span className="text-gray-500 text-sm ml-1">pts</span>
                            </div>
                            
                            <button
                              onClick={() => handleRedeem(item)}
                              disabled={!canAfford || isOutOfStock || redeeming === item.id || !user}
                              className={`px-4 py-2 rounded font-mono text-sm uppercase transition-colors flex items-center gap-2 ${
                                canAfford && !isOutOfStock && user
                                  ? 'bg-[#C5A52A] hover:bg-[#d4b43a] text-black'
                                  : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {redeeming === item.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Redeem'
                              )}
                            </button>
                          </div>
                          
                          {!canAfford && !isOutOfStock && user && (
                            <div className="text-xs text-red-400 mt-2 font-mono">
                              Need {(item.points_cost - userPoints.available_points).toLocaleString()} more points
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'history' && (
            <div>
              <h2 className="text-xl font-bold mb-6">Your Redemption History</h2>
              
              {!user ? (
                <div className="text-center py-12 bg-gray-900 border border-gray-800 rounded-lg">
                  <p className="text-gray-400">Please log in to see your redemptions</p>
                </div>
              ) : redemptions.length === 0 ? (
                <div className="text-center py-12 bg-gray-900 border border-gray-800 rounded-lg">
                  <Gift className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400">No redemptions yet</p>
                  <p className="text-gray-500 text-sm mt-1">Browse the store and redeem your points!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {redemptions.map((redemption) => (
                    <div 
                      key={redemption.id}
                      className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded ${
                          redemption.status === 'fulfilled' ? 'bg-green-500/20 text-green-400' :
                          redemption.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {redemption.status === 'fulfilled' ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <Loader2 className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold">
                            {redemption.store_items?.name || 'Unknown Item'}
                          </div>
                          <div className="text-sm text-gray-400">
                            {new Date(redemption.redeemed_at).toLocaleDateString()} • {redemption.points_spent} points
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className={`text-xs font-mono uppercase px-2 py-1 rounded ${
                          redemption.status === 'fulfilled' ? 'bg-green-900/30 text-green-400' :
                          redemption.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                          'bg-gray-800 text-gray-400'
                        }`}>
                          {redemption.status}
                        </span>
                        
                        {redemption.fulfillment_data?.affiliate_url && (
                          <a
                            href={redemption.fulfillment_data.affiliate_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 justify-end"
                          >
                            Claim Reward <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Shipping Modal for Physical Items */}
        {showShippingModal && pendingItem && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-xl font-bold text-white">Shipping Information</h3>
                <p className="text-gray-400 text-sm mt-1">
                  We need your address to ship: <span className="text-[#C5A52A]">{pendingItem.name}</span>
                </p>
              </div>
              
              <form onSubmit={handleShippingSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={shippingInfo.full_name}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, full_name: e.target.value })}
                    required
                    placeholder="John Doe"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Address Line 1 *</label>
                  <input
                    type="text"
                    value={shippingInfo.address_line1}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, address_line1: e.target.value })}
                    required
                    placeholder="123 Main Street"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={shippingInfo.address_line2}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, address_line2: e.target.value })}
                    placeholder="Apt 4B (optional)"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">City *</label>
                    <input
                      type="text"
                      value={shippingInfo.city}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                      required
                      placeholder="Regina"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Province *</label>
                    <input
                      type="text"
                      value={shippingInfo.province}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, province: e.target.value })}
                      required
                      placeholder="SK"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Postal Code *</label>
                    <input
                      type="text"
                      value={shippingInfo.postal_code}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, postal_code: e.target.value })}
                      required
                      placeholder="S4P 0B3"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={shippingInfo.phone}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                      placeholder="306-555-1234"
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                    />
                  </div>
                </div>

                {/* Size/Color Selection */}
                {(pendingItem.size_options || pendingItem.color_options) && (
                  <div className="grid grid-cols-2 gap-4">
                    {pendingItem.size_options && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Size *</label>
                        <select
                          value={shippingInfo.selected_size}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, selected_size: e.target.value })}
                          required
                          className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                        >
                          <option value="">Select Size</option>
                          {pendingItem.size_options.split(',').map(size => (
                            <option key={size.trim()} value={size.trim()}>{size.trim()}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {pendingItem.color_options && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Color *</label>
                        <select
                          value={shippingInfo.selected_color}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, selected_color: e.target.value })}
                          required
                          className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                        >
                          <option value="">Select Color</option>
                          {pendingItem.color_options.split(',').map(color => (
                            <option key={color.trim()} value={color.trim()}>{color.trim()}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Notes <span className="text-gray-600">(special instructions)</span>
                  </label>
                  <textarea
                    value={shippingInfo.notes}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, notes: e.target.value })}
                    rows={2}
                    placeholder="Any special requests or instructions"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                  />
                </div>
                
                <div className="bg-gray-800/50 rounded p-3 text-sm text-gray-400">
                  <p>Cost: <span className="text-[#C5A52A] font-bold">{pendingItem.points_cost} points</span></p>
                  <p className="mt-1">Your balance after: {userPoints.available_points - pendingItem.points_cost} points</p>
                </div>

                {/* Save to profile checkbox */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveToProfile}
                    onChange={(e) => setSaveToProfile(e.target.checked)}
                    className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-[#C5A52A] focus:ring-[#C5A52A]"
                  />
                  <span className="text-sm text-gray-400">Save this address to my profile for future orders</span>
                </label>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowShippingModal(false); setPendingItem(null); }}
                    className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={redeeming}
                    className="flex-1 px-4 py-2 bg-[#C5A52A] hover:bg-[#d4b43a] text-black font-bold rounded transition-colors flex items-center justify-center gap-2"
                  >
                    {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm & Redeem'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Image Lightbox */}
        {lightboxImage && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 cursor-zoom-out"
            onClick={() => setLightboxImage(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <img 
                src={lightboxImage} 
                alt="Product" 
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              <button
                onClick={() => setLightboxImage(null)}
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl transition-colors"
              >
                ×
              </button>
              <p className="text-center text-gray-400 text-sm mt-3">Click anywhere to close</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PointsStore;
